/**
 * Pure projections behind the PHONE review cards of a production record
 * (plan-06-mobile.md, lane B).
 *
 * The eleven-column table does not fit a phone, so a row (one item x age
 * band) becomes a card whose body is three short lines, one per column
 * group: `Planned 98 · Prepared 97`. These helpers turn a
 * spreadsheet row view (the INPUT side, `RecordSpreadsheetRowView`) or a
 * document matrix row (the READ-ONLY side, `MatrixItemRow`) into that line
 * shape; the components only lay the chips out.
 *
 * Type-only imports on purpose: this module must load without a DOM, so it
 * never pulls the React table module in at runtime.
 */

import type {
  MatrixCheckpoint,
  MatrixItemRow,
  MatrixTempCell,
  PrintBandRow,
} from '@/components/production/record-insights'
import type {
  RecordCell,
  RecordColumnKey,
  RecordSpreadsheetRowView,
} from '@/components/production/spreadsheet/record-spreadsheet-table'

export type ValueGroup = 'Production' | 'Service' | 'Temps'

export interface ValueChip {
  /** The short column name shown muted before the value (`Ordered`). */
  label: string
  /** The value as the card prints it (`98`, `—`, `N/A`, `140.0°`, `47.5° → 39.0°`). */
  text: string
  /** A low-confidence photo read (input side) or an out-of-range reading (document side). */
  flagged: boolean
  tone?: 'muted' | 'destructive' | 'success'
  /** The required band when a reading is out of range (`needs ≥ 135°`), document side only. */
  note?: string
}

export interface ValueLine {
  group: ValueGroup
  chips: ValueChip[]
}

/** Null / empty counts print as the digital record's em dash: "not recorded", never zero. */
export const EMPTY_VALUE_TEXT = '—'
/** A temperature the item does not take (shelf-stable): considered, nothing to read. */
export const NOT_OFFERED_TEXT = 'N/A'

/** Planned and Prepared (the meal's on-hand count); the kitchen's prepared count is not shown (2026-11-04). */
const TRANSPORT_COLUMNS: ReadonlyArray<readonly [RecordColumnKey, string]> = [
  ['ordered', 'Planned'],
  ['received', 'Prepared'],
]
const SERVICE_COLUMNS: ReadonlyArray<readonly [RecordColumnKey, string]> = [
  ['served', 'Served'],
  ['reimbursable', 'Reimb.'],
  ['nonReimbursable', 'Non-reimb.'],
  ['leftover', 'Leftover'],
]
const TEMP_COLUMNS: ReadonlyArray<readonly [RecordColumnKey, string]> = [
  ['kitchenTemp', 'Kitchen'],
  ['receivedTemp', 'Received'],
  ['servingTemp', 'Serving'],
]

const CHECKPOINT_LABEL: Record<MatrixCheckpoint, string> = {
  FACILITY: 'Kitchen',
  DELIVERY: 'Received',
  SERVICE: 'Serving',
}

// ---------------------------------------------------------------------------
// Input side: a spreadsheet row view
// ---------------------------------------------------------------------------

/**
 * A cell the flow disabled WITH an explanation (`This item is not on the
 * production record.`) has nowhere to save and is left off the card. A cell
 * disabled without one is merely locked (signed / busy) and still shows
 * its value.
 */
function cellOmitted(cell: RecordCell): boolean {
  return cell.disabled === true && cell.title !== undefined
}

/** `140` → `140.0°`, the digital record's own temperature display. */
export function formatMatrixTemp(value: string | number): string {
  const num = Number(value)
  return Number.isNaN(num) ? String(value) : `${num.toFixed(1)}°`
}

function cellChip(label: string, cell: RecordCell): ValueChip {
  const flagged = cell.flag === 'check'
  if (cell.kind === 'count') {
    return cell.value === ''
      ? { label, text: EMPTY_VALUE_TEXT, flagged, tone: 'muted' }
      : { label, text: String(cell.value), flagged }
  }
  if (!cell.offered) {
    return { label, text: NOT_OFFERED_TEXT, flagged, tone: 'muted' }
  }
  return cell.value === null
    ? { label, text: EMPTY_VALUE_TEXT, flagged, tone: 'muted' }
    : { label, text: formatMatrixTemp(cell.value), flagged }
}

function groupLine(
  group: ValueGroup,
  columns: ReadonlyArray<readonly [RecordColumnKey, string]>,
  cells: RecordSpreadsheetRowView['cells'],
): ValueLine | null {
  const chips = columns.flatMap(([key, label]) => {
    const cell = cells[key]
    return cellOmitted(cell) ? [] : [cellChip(label, cell)]
  })
  return chips.length === 0 ? null : { group, chips }
}

/**
 * Three lines (Production / Service / Temps) from a row view's cells. An
 * empty count prints `—`, a temperature the item does not take prints `N/A`,
 * an unset temperature prints `—`, and a `check` flag marks the chip. A
 * group whose every cell is off the record is dropped.
 */
export function rowValueLines(row: RecordSpreadsheetRowView): ValueLine[] {
  return [
    groupLine('Production', TRANSPORT_COLUMNS, row.cells),
    groupLine('Service', SERVICE_COLUMNS, row.cells),
    groupLine('Temps', TEMP_COLUMNS, row.cells),
  ].filter((line): line is ValueLine => line !== null)
}

/** The card's one-line legend, the table's own (`K-8 · 1 each`). */
export function rowLegend(row: RecordSpreadsheetRowView): string {
  return row.legend
}

// ---------------------------------------------------------------------------
// Document side: a matrix row
// ---------------------------------------------------------------------------

/** The required hold band, worded as the digital record words it. */
export function tempBandNote(band: MatrixTempCell['readings'][number]['band']): string | undefined {
  if (band === 'HOT') {
    return 'needs ≥ 135°'
  }
  if (band === 'CHILLED') {
    return 'needs ≤ 41°'
  }
  return undefined
}

function countChip(label: string, value: number | null): ValueChip {
  return value === null
    ? { label, text: EMPTY_VALUE_TEXT, flagged: false, tone: 'muted' }
    : { label, text: String(value), flagged: false }
}

function tempChip(label: string, cell: MatrixTempCell | undefined): ValueChip {
  if (!cell) {
    return { label, text: EMPTY_VALUE_TEXT, flagged: false, tone: 'muted' }
  }
  const text = cell.readings.map((r) => formatMatrixTemp(r.value)).join(' → ')
  if (cell.outOfRange) {
    const band = cell.readings.find((r) => r.status === 'OUT_OF_RANGE')?.band ?? null
    const note = tempBandNote(band)
    return note === undefined
      ? { label, text, flagged: true, tone: 'destructive' }
      : { label, text, flagged: true, tone: 'destructive', note }
  }
  if (cell.readings.every((r) => r.status === 'NOT_RECORDED')) {
    return { label, text, flagged: false, tone: 'muted' }
  }
  return { label, text, flagged: false, tone: 'success' }
}

/** The counts one card line set reads: a matrix row's, or one printed band row's. */
interface CountSource {
  ordered: number | null
  prepared: number | null
  delivered: number | null
  served: number | null
  reimbursable: number | null
  nonReimbursable: number | null
  leftover: number | null
}

function countLines(counts: CountSource, temps: MatrixItemRow['temps'], checkpoints: MatrixCheckpoint[]): ValueLine[] {
  const served = countChip('Served', counts.served)
  const lines: ValueLine[] = [
    {
      // Planned · Prepared (2026-11-04): Prepared is the meal's on-hand count
      // (`delivered`); the kitchen's own prepared count is not shown.
      group: 'Production',
      chips: [countChip('Planned', counts.ordered), countChip('Prepared', counts.delivered)],
    },
    {
      group: 'Service',
      chips: [
        served,
        countChip('Reimb.', counts.reimbursable),
        countChip('Non-reimb.', counts.nonReimbursable),
        countChip('Leftover', counts.leftover),
      ],
    },
  ]
  if (checkpoints.length > 0) {
    lines.push({ group: 'Temps', chips: checkpoints.map((cp) => tempChip(CHECKPOINT_LABEL[cp], temps[cp])) })
  }
  return lines
}

/**
 * The read-only twin of `rowValueLines` over the document matrix: the same
 * three lines for the day-details page. A re-checked temperature reads
 * `47.5° → 39.0°`; an out-of-range reading is flagged, destructive, and
 * carries the band note. Temps render only for the checkpoints the matrix
 * has readings for, and the group is dropped when there are none. The old
 * `▼N` receiving-shortfall marker is gone (Sheila, 2026-10-08: "the labeled
 * decrease is confusing").
 */
export function matrixValueLines(row: MatrixItemRow, checkpoints: MatrixCheckpoint[]): ValueLine[] {
  return countLines(row, row.temps, checkpoints)
}

/**
 * The same three lines for ONE printed band row (the phone shows the record
 * as grade-group blocks of band rows, like the paper — PROD-1441): the band's
 * own counts, the item's temperatures, and the Served chip flagged when it
 * exceeds the band's Prepared.
 */
export function printRowValueLines(printRow: PrintBandRow, checkpoints: MatrixCheckpoint[]): ValueLine[] {
  return countLines(printRow, printRow.row.temps, checkpoints)
}

/**
 * One tier sub-row as a single legend line for the card
 * (`K-5 · planned 25 · prepared 25 · served 22 · reimb 22 · non-reimb 0 · left 3`);
 * null figures are left out rather than printed as dashes. A serving label
 * that merely repeats the band is dropped, as the printed record drops it.
 */
export function tierLine(tier: MatrixItemRow['tiers'][number]): string {
  const servingLabel =
    tier.servingLabel && tier.servingLabel !== tier.label && tier.servingLabel !== tier.gradeRangeName
      ? tier.servingLabel
      : null
  const head = servingLabel ? `${tier.label} ${servingLabel}` : tier.label
  const parts: Array<[string, number | null]> = [
    ['planned', tier.ordered],
    ['prepared', tier.delivered],
    ['served', tier.served],
    ['reimb', tier.reimbursable],
    ['non-reimb', tier.nonReimbursable],
    ['left', tier.leftover],
  ]
  return [head, ...parts.filter(([, value]) => value !== null).map(([name, value]) => `${name} ${value}`)].join(' · ')
}
